import { useMemo } from "react";
import { useDailySalesQuery } from "../../query/adminDailySalesQuery";
import { formatMoney } from "../../utils/formatMoney";
import { isSalesToday } from "../../utils/dailySalesUtils";
import { SalesPieChartCard } from "./SalesPieChartCard";
import { useTranslation } from "react-i18next";

interface SalesCurrencyPieChartProps {
  resellerId?: string;
  date: string;
  selectedDateLabel: string;
}

export const SalesCurrencyPieChart = ({
  resellerId,
  date,
  selectedDateLabel,
}: SalesCurrencyPieChartProps) => {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useDailySalesQuery(date, date, resellerId);
  const isToday = isSalesToday(date);

  const chartData = useMemo(() => {
    const dayStats = stats?.[0];
    if (!dayStats?.grossAmountByCurrency) return [];
    return Object.values(dayStats.grossAmountByCurrency)
      .filter((money) => money.amount > 0)
      .map((money) => ({
        key: money.currency,
        name: money.currency,
        value: money.amount,
        label: formatMoney(money),
      }));
  }, [stats]);

  const chartTitle = isToday
    ? t("adminDailySales.salesByCurrencyToday")
    : t("adminDailySales.salesByCurrencyOnDate", { date: selectedDateLabel });

  const emptyMessage = isToday
    ? t("adminDailySales.noDataToday")
    : t("adminDailySales.noDataOnDate", { date: selectedDateLabel });

  return (
    <SalesPieChartCard
      title={chartTitle}
      emptyMessage={emptyMessage}
      isLoading={isLoading}
      chartData={chartData}
    />
  );
};
